import { useState, lazy, Suspense, useEffect, useMemo, Component } from "react";
import { Route, Switch, useLocation, Redirect } from "wouter";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Navigation } from "@/components/navigation";
import { ShoppingCart } from "@/components/shopping-cart";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { Footer } from "@/components/footer";
import { useScrollToTop } from "@/hooks/use-scroll-to-top";
import { AudioManagerProvider } from "@/contexts/AudioManagerContext";
import { NetworkStatus } from "@/components/network-status";
import { PullToRefresh } from "@/components/pull-to-refresh";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";

// Error Boundary Component
class ErrorBoundary extends Component<
  {
    children: React.ReactNode;
    fallback: (props: { error: Error }) => React.ReactNode;
  },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error("❌ Error boundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return this.props.fallback({ error: this.state.error });
    }
    return this.props.children;
  }
}

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-sage-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Eager load only the home page for fast initial render
import Home from "@/pages/home";

// Utility function to retry chunk loading
const retryImport = (importFn: () => Promise<any>, retries = 3) => {
  return new Promise((resolve, reject) => {
    importFn()
      .then(resolve)
      .catch((error) => {
        if (retries === 0) {
          reject(error);
          return;
        }
        console.log(`Retrying import... (${retries} attempts left)`);
        setTimeout(() => {
          retryImport(importFn, retries - 1).then(resolve, reject);
        }, 1000);
      });
  });
};

// Lazy load all other pages with retry logic
const Mindfulness = lazy(() =>
  retryImport(() => import("./pages/mindfulness")),
);
const GuidedMeditation = lazy(() =>
  retryImport(() => import("./pages/guided-meditation")),
);
const SleepStories = lazy(() =>
  retryImport(() => import("./pages/sleep-stories")),
);
const SoundTherapy = lazy(() =>
  retryImport(() => import("./pages/sound-therapy")),
);
const StressRelief = lazy(() =>
  retryImport(() => import("./pages/stress-relief")),
);
const ProgressiveMuscleRelaxation = lazy(() =>
  retryImport(() => import("./pages/progressive-muscle-relaxation")),
);
const MindfulStressRelease = lazy(() =>
  retryImport(() => import("./pages/mindful-stress-release")),
);
const FocusClarity = lazy(() =>
  retryImport(() => import("./pages/focus-clarity")),
);
const EnergyBoost = lazy(() =>
  retryImport(() => import("./pages/energy-boost")),
);
const SleepRest = lazy(() => retryImport(() => import("./pages/sleep-rest")));
const Shop = lazy(() => retryImport(() => import("@/pages/shop")));
const Booking = lazy(() => retryImport(() => import("@/pages/booking")));
const NotFound = lazy(() => retryImport(() => import("@/pages/not-found")));
const BreathingExercises = lazy(() =>
  retryImport(() => import("@/pages/breathing-exercises")),
);
const BreathingTechnique = lazy(() =>
  retryImport(() => import("@/pages/breathing-technique")),
);
const SoundTherapyLibrary = lazy(() =>
  retryImport(() => import("@/pages/sound-therapy-library")),
);
const Experts = lazy(() => retryImport(() => import("@/pages/experts")));
const Therapists = lazy(() => retryImport(() => import("@/pages/therapists")));
const TherapistProfile = lazy(() =>
  retryImport(() => import("@/pages/therapist-profile")),
);
const MyAccount = lazy(() => retryImport(() => import("@/pages/my-account")));
const AISession = lazy(() => retryImport(() => import("@/pages/ai-session")));
const HumanSession = lazy(() =>
  retryImport(() => import("@/pages/human-session")),
);
const GroupSession = lazy(() =>
  retryImport(() => import("@/pages/group-session")),
);
const Terms = lazy(() => retryImport(() => import("@/pages/terms")));
const Contact = lazy(() => retryImport(() => import("@/pages/contact")));
const About = lazy(() => retryImport(() => import("@/pages/about")));
const AboutHealthAI = lazy(() =>
  retryImport(() => import("@/pages/about-health-ai")),
);
const AIHealthSession = lazy(() =>
  retryImport(() => import("@/pages/ai-health-session")),
);
const ProductRecommendations = lazy(() =>
  retryImport(() => import("@/pages/product-recommendations")),
);
const Login = lazy(() => retryImport(() => import("@/pages/login")));
const AdminLogin = lazy(() => retryImport(() => import("@/pages/admin-login")));
const TherapistLogin = lazy(() =>
  retryImport(() => import("@/pages/therapist-login")),
);
const QRAnalytics = lazy(() =>
  retryImport(() => import("@/pages/qr-analytics")),
);
const QRManager = lazy(() => retryImport(() => import("@/pages/qr-manager")));
const AdminConsole = lazy(() => retryImport(() => import("@/pages/admin")));
const TherapistDashboard = lazy(() =>
  retryImport(() => import("./pages/therapist-dashboard")),
);
const StressReliefBreathing = lazy(() =>
  retryImport(() => import("@/pages/stress-relief-breathing")),
);
const BodyScanStress = lazy(() =>
  retryImport(() => import("@/pages/body-scan-stress")),
);
const SinglePointFocus = lazy(() =>
  retryImport(() => import("./pages/single-point-focus")),
);
const MindfulAttentionTraining = lazy(() =>
  retryImport(() => import("./pages/mindful-attention-training")),
);
const ClarityMindMeditation = lazy(() =>
  retryImport(() => import("./pages/clarity-mind-meditation")),
);
const ConcentrationMastery = lazy(() =>
  retryImport(() => import("./pages/concentration-mastery")),
);
const EnergizingBreathWork = lazy(() =>
  retryImport(() => import("./pages/energizing-breath-work")),
);
const MorningActivation = lazy(() =>
  retryImport(() => import("./pages/morning-activation")),
);
const MiddayRevival = lazy(() =>
  retryImport(() => import("./pages/midday-revival")),
);
const PowerMeditation = lazy(() =>
  retryImport(() => import("./pages/power-meditation")),
);
const ForgotPassword = lazy(() =>
  retryImport(() => import("./pages/forgot-password")),
);
const ResetPassword = lazy(() =>
  retryImport(() => import("./pages/reset-password")),
);
const AddTherapist = lazy(() =>
  retryImport(() => import("@/pages/add-therapist")),
);

// Wrapper component to handle scroll behavior inside Router context
function ScrollToTopWrapper({ children }: { children: React.ReactNode }) {
  useScrollToTop();
  return <>{children}</>;
}

function Router() {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [location] = useLocation();

  // Check if current route is an admin route or therapist login
  const isAdminRoute =
    location.startsWith("/admin") ||
    location === "/qr-analytics" ||
    location === "/qr-manager" ||
    location === "/therapist-login" ||
    location.startsWith("/therapist-dashboard");

  const isBookingRoute = location.startsWith("/booking");

  const excludePullToRefresh = useMemo(() => {
    const excluded = [
      "/booking",
      "/ai-session",
      "/ai-health-session",
      "/human-session",
      "/group-session",
      "/login",
      "/admin-login",
      "/therapist-login",
      "/forgot-password",
      "/reset-password",
      "/breathing-technique",
      "/breathing-exercises",
      "/guided-meditation",
      "/sound-therapy",
      "/progressive-muscle-relaxation",
      "/mindful-stress-release",
      "/stress-relief-breathing",
      "/body-scan-stress",
      "/single-point-focus",
      "/mindful-attention-training",
      "/clarity-mind-meditation",
      "/concentration-mastery",
      "/energizing-breath-work",
      "/morning-activation",
      "/midday-revival",
      "/power-meditation",
      "/focus-clarity",
      "/energy-boost",
      "/sleep-rest",
      "/sleep-stories",
    ];
    return excluded.some(
      (path) => location === path || location.startsWith(path + "/"),
    );
  }, [location]);

  return (
    <ScrollToTopWrapper>
      <PullToRefresh enabled={!excludePullToRefresh}>
        <div className="min-h-screen flex flex-col">
          {!isAdminRoute && (
            <Navigation onCartClick={() => setIsCartOpen(true)} />
          )}
          <NetworkStatus />
          <main className="flex-1 pb-20 md:pb-0">
            <Suspense fallback={<PageLoader />}>
              <Switch>
                <Route path="/" component={Home} />
                <Route path="/home">
                  <Redirect to="/" />
                </Route>
                <Route path="/login" component={Login} />
                <Route path="/forgot-password" component={ForgotPassword} />
                <Route path="/reset-password" component={ResetPassword} />
                {/* Added mindfulness route here */}
                <Route path="/mindfulness" component={Mindfulness} />
                <Route path="/guided-meditation" component={GuidedMeditation} />
                <Route path="/stress-relief" component={StressRelief} />
                <Route
                  path="/progressive-muscle-relaxation"
                  component={ProgressiveMuscleRelaxation}
                />
                <Route
                  path="/mindful-stress-release"
                  component={MindfulStressRelease}
                />
                <Route
                  path="/stress-relief-breathing"
                  component={StressReliefBreathing}
                />
                <Route path="/body-scan-stress" component={BodyScanStress} />
                <Route
                  path="/single-point-focus"
                  component={SinglePointFocus}
                />
                <Route
                  path="/mindful-attention-training"
                  component={MindfulAttentionTraining}
                />
                <Route
                  path="/clarity-mind-meditation"
                  component={ClarityMindMeditation}
                />
                <Route
                  path="/concentration-mastery"
                  component={ConcentrationMastery}
                />

                {/* Energy Boost Session Routes */}
                <Route
                  path="/energizing-breath-work"
                  component={EnergizingBreathWork}
                />
                <Route
                  path="/morning-activation"
                  component={MorningActivation}
                />
                <Route path="/midday-revival" component={MiddayRevival} />
                <Route path="/power-meditation" component={PowerMeditation} />
                <Route path="/focus-clarity" component={FocusClarity} />
                <Route path="/energy-boost" component={EnergyBoost} />
                <Route path="/sleep-rest" component={SleepRest} />
                <Route path="/sleep-stories" component={SleepStories} />
                <Route path="/sound-therapy" component={SoundTherapy} />
                <Route
                  path="/sound-therapy/library"
                  component={SoundTherapyLibrary}
                />
                <Route
                  path="/sound-therapy-library"
                  component={SoundTherapyLibrary}
                />
                <Route path="/shop" component={Shop} />
                <Route path="/booking" component={Booking} />
                <Route
                  path="/breathing-exercises"
                  component={BreathingExercises}
                />
                <Route
                  path="/breathing-technique/:id"
                  component={BreathingTechnique}
                />
                <Route path="/experts" component={Experts} />
                <Route path="/therapists" component={Therapists} />
                <Route
                  path="/therapist-profile/:slug"
                  component={TherapistProfile}
                />
                <Route path="/terms-privacy-policy" component={Terms} />
                <Route path="/contact" component={Contact} />
                <Route path="/about" component={About} />
                <Route path="/about-health-ai" component={AboutHealthAI} />
                <Route
                  path="/product-recommendations"
                  component={ProductRecommendations}
                />
                <Route path="/ai-health-session" component={AIHealthSession} />
                <Route
                  path="/qr"
                  component={() => {
                    const [location] = useLocation();

                    // Get the full URL with query parameters
                    const fullUrl = window.location.href;
                    const urlObj = new URL(fullUrl);
                    const params = urlObj.searchParams;

                    const campaign = params.get("campaign") || "default";
                    const source = params.get("source") || "unknown";
                    const destination = params.get("destination") || "";

                    console.log("QR Code Scanned:", {
                      fullUrl,
                      campaign,
                      source,
                      destination,
                      allParams: Object.fromEntries(params.entries()),
                    });

                    // Determine redirect destination based on priority:
                    // 1. Custom destination (if provided)
                    // 2. Campaign-specific defaults
                    let redirectTo = "/";

                    if (destination) {
                      // Decode the destination URL
                      redirectTo = decodeURIComponent(destination);
                    } else if (campaign === "booking") {
                      redirectTo = "/booking";
                    } else if (campaign === "ai-session") {
                      redirectTo = "/booking?type=ai";
                    } else if (campaign === "mindfulness") {
                      redirectTo = "/mindfulness";
                    } else if (campaign === "sound-therapy") {
                      redirectTo = "/sound-therapy";
                    } else if (campaign === "shop") {
                      redirectTo = "/shop";
                    }

                    // Track QR scan with all parameters
                    const scanData = {
                      campaign: campaign,
                      source: source,
                      destination: redirectTo,
                    };

                    console.log("Tracking QR scan:", scanData);
                    console.log("Redirecting to:", redirectTo);

                    fetch("/api/qr/scan", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify(scanData),
                    }).catch((err) =>
                      console.error("Failed to track QR scan:", err),
                    );

                    // Redirect after tracking
                    setTimeout(() => {
                      window.location.href = redirectTo;
                    }, 500);

                    return (
                      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sage-50 to-white">
                        <div className="text-center">
                          <div className="w-16 h-16 border-4 border-sage-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                          <p className="text-gray-600 mb-2">
                            Redirecting to your destination...
                          </p>
                          <p className="text-xs text-gray-400">
                            Campaign: {campaign} | Source: {source}
                          </p>
                        </div>
                      </div>
                    );
                  }}
                />

                {/* Protected Routes */}
                <Route path="/admin-login" component={AdminLogin} />
                <Route path="/therapist-login" component={TherapistLogin} />
                <Route path="/therapists-login">
                  <Redirect to="/therapist-login" />
                </Route>
                <Route path="/admin">
                  {() => (
                    <ProtectedRoute requiredRole="admin">
                      <AdminConsole />
                    </ProtectedRoute>
                  )}
                </Route>
                <Route path="/therapist-dashboard">
                  {() => (
                    <ProtectedRoute requiredRole="therapist">
                      <TherapistDashboard />
                    </ProtectedRoute>
                  )}
                </Route>

                <Route path="/qr-analytics">
                  {() => (
                    <ProtectedRoute roles={["admin"]}>
                      <QRAnalytics />
                    </ProtectedRoute>
                  )}
                </Route>
                <Route path="/qr-manager">
                  {() => (
                    <ProtectedRoute roles={["admin"]}>
                      <QRManager />
                    </ProtectedRoute>
                  )}
                </Route>
                <Route path="/booking">
                  {() => (
                    <ProtectedRoute>
                      <Booking />
                    </ProtectedRoute>
                  )}
                </Route>
                <Route path="/my-bookings">
                  <ProtectedRoute>
                    <MyAccount />
                  </ProtectedRoute>
                </Route>
                <Route path="/my-account">
                  <ProtectedRoute>
                    <MyAccount />
                  </ProtectedRoute>
                </Route>
                <Route path="/my-account/sessions">
                  {() => {
                    const MyAccountSessions = lazy(
                      () => import("@/pages/my-account/sessions"),
                    );
                    return (
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <MyAccountSessions />
                        </Suspense>
                      </ProtectedRoute>
                    );
                  }}
                </Route>
                <Route path="/my-account/profile">
                  {() => {
                    const MyAccountProfile = lazy(
                      () => import("@/pages/my-account/profile"),
                    );
                    return (
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <MyAccountProfile />
                        </Suspense>
                      </ProtectedRoute>
                    );
                  }}
                </Route>
                <Route path="/my-account/security">
                  {() => {
                    const MyAccountSecurity = lazy(
                      () => import("@/pages/my-account/security"),
                    );
                    return (
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <MyAccountSecurity />
                        </Suspense>
                      </ProtectedRoute>
                    );
                  }}
                </Route>
                <Route path="/my-account/subscription">
                  {() => {
                    const MySubscription = lazy(
                      () => import("@/pages/my-account/subscription"),
                    );
                    return (
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <MySubscription />
                        </Suspense>
                      </ProtectedRoute>
                    );
                  }}
                </Route>
                <Route path="/my-account/notifications">
                  {() => {
                    const MyAccountNotifications = lazy(
                      () => import("@/pages/my-account/notifications"),
                    );
                    return (
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <MyAccountNotifications />
                        </Suspense>
                      </ProtectedRoute>
                    );
                  }}
                </Route>
                <Route path="/my-account/language">
                  {() => {
                    const MyAccountLanguage = lazy(
                      () => import("@/pages/my-account/language"),
                    );
                    return (
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <MyAccountLanguage />
                        </Suspense>
                      </ProtectedRoute>
                    );
                  }}
                </Route>
                <Route path="/ai-session">
                  <ProtectedRoute>
                    <AISession />
                  </ProtectedRoute>
                </Route>
                <Route path="/human-session">
                  <ProtectedRoute>
                    <HumanSession />
                  </ProtectedRoute>
                </Route>
                <Route path="/group-session">
                  <ProtectedRoute>
                    <GroupSession />
                  </ProtectedRoute>
                </Route>
                {/* Add Therapist Route nested within Admin */}
                <Route path="/admin/add-therapist">
                  {() => (
                    <ProtectedRoute requiredRole="admin">
                      <AddTherapist />
                    </ProtectedRoute>
                  )}
                </Route>

                {/* Edit Therapist Route with ID parameter */}
                <Route path="/add-therapist/:therapistId">
                  {() => (
                    <ProtectedRoute requiredRole="admin">
                      <AddTherapist />
                    </ProtectedRoute>
                  )}
                </Route>

                <Route component={NotFound} />
              </Switch>
            </Suspense>
          </main>
          {!isAdminRoute && !isBookingRoute && <Footer />}
          <ShoppingCart
            isOpen={isCartOpen}
            onClose={() => setIsCartOpen(false)}
          />
        </div>
      </PullToRefresh>
    </ScrollToTopWrapper>
  );
}

function App() {
  useEffect(() => {
    // Handle chunk load failures by reloading the page once
    const handleChunkError = (event: ErrorEvent) => {
      const isChunkLoadError =
        event.message?.includes(
          "Failed to fetch dynamically imported module",
        ) ||
        event.message?.includes("Importing a module script failed") ||
        event.message?.includes("dynamically imported module");

      if (isChunkLoadError && !sessionStorage.getItem("chunk-error-reload")) {
        sessionStorage.setItem("chunk-error-reload", "true");
        console.log("🔄 Chunk load error detected, reloading page...");
        window.location.reload();
      }
    };

    window.addEventListener("error", handleChunkError);

    // Clear reload flag after successful load
    sessionStorage.removeItem("chunk-error-reload");

    // Deep link handler for Capacitor
    CapacitorApp.addListener("appUrlOpen", (event) => {
      const url = event.url;
      console.log("🔗 Deep link opened:", url);

      if (url.startsWith("https://healthyowl.in")) {
        const path = url.replace("https://healthyowl.in", "") || "/";
        console.log("➡️ Navigating to:", path);
        window.location.href = path;
      }
      // Custom scheme — iOS OAuth redirect back to app
      if (url.startsWith("in.healthyowl.mitr://")) {
        const path = url.replace("in.healthyowl.mitr:/", "") || "/";
        console.log("➡️ App scheme navigating to:", path);
        window.location.href = path;
      }
    });

    // Push Notifications
    // ✅ Only run push logic on native platforms (Android / iOS)
    if (Capacitor.isNativePlatform()) {
      PushNotifications.requestPermissions().then((result) => {
        if (result.receive === "granted") {
          PushNotifications.register();
        }
      });

      PushNotifications.addListener("registration", (token) => {
        console.log("📲 Push Token:", token.value);
        // TODO: send token to backend later
      });

      PushNotifications.addListener("registrationError", (error) => {
        console.error("❌ Push registration error:", error);
      });

      PushNotifications.addListener(
        "pushNotificationReceived",
        (notification) => {
          console.log("🔔 Notification received:", notification);
        },
      );

      PushNotifications.addListener(
        "pushNotificationActionPerformed",
        (notification) => {
          console.log("👉 Notification tapped:", notification);
        },
      );
    } else {
      console.log("🌐 Push notifications disabled on web");
    }

    return () => window.removeEventListener("error", handleChunkError);
  }, []);

  return (
    <AudioManagerProvider>
      <ErrorBoundary
        fallback={({ error }) => {
          const isChunkError =
            error.message?.includes(
              "Failed to fetch dynamically imported module",
            ) ||
            error.message?.includes("Importing a module script failed") ||
            error.message?.includes("dynamically imported module");

          return (
            <div className="min-h-screen flex items-center justify-center p-4">
              <div className="text-center max-w-md">
                <h1 className="text-2xl font-bold text-red-600 mb-4">
                  {isChunkError ? "Update Available" : "Something went wrong"}
                </h1>
                <p className="text-gray-600 mb-4">
                  {isChunkError
                    ? "A new version is available. Please refresh the page."
                    : error.message || "An unexpected error occurred"}
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                >
                  Reload Page
                </button>
              </div>
            </div>
          );
        }}
      >
        <div className="min-h-screen flex flex-col">
          <Router />
        </div>
        <Toaster />
        <NetworkStatus />
      </ErrorBoundary>
    </AudioManagerProvider>
  );
}

export default App;
